import { ReactNode } from 'react';

import { Box, createUseVariantStyle, forwardRef, rem, StyleProps } from '@bonusly/design-system';

interface ChipProps extends StyleProps {
	as?: any;
	children: ReactNode;
	isInteractive?: boolean;
	onClick?: VoidFunction;
	size?: 'xs' | 'sm' | 'lg';
}

interface FlaggedProps {
	as: any;
	_hover?: StyleProps;
}

const getFlaggedProps = ({ isInteractive }: Pick<ChipProps, 'isInteractive'>): FlaggedProps => ({
	as: 'span',
	...(isInteractive && {
		as: 'button',
		_hover: {
			backgroundColor: 'green.400',
			color: 'green.700',
		},
	}),
});

const useChipVariantStyle = createUseVariantStyle({
	size: {
		xs: {
			h: rem(28),
		},
		sm: {},
		md: {
			h: rem(40),
		},
		lg: {
			h: rem(48),
		},
	},
	variant: {
		gray: {
			backgroundColor: 'blackAlpha.50',
			color: 'gray.600',
		},
		green: {
			backgroundColor: 'green.400',
			color: 'green.700',
		},
		amount: {},
	},
});

const useTextVariantStyle = createUseVariantStyle({
	size: {
		xs: {
			fontSize: 'lg',
			px: 2,
		},
		sm: {},
		md: {
			fontSize: 'md',
			px: 4,
		},
		lg: {
			fontSize: rem(24),
			px: 4,
			lineHeight: 32 / 24,
		},
	},
	variant: {
		amount: {
			fontFamily: 'display',
			fontWeight: 600,
		},
	},
});

export const Chip = forwardRef(({ children, isInteractive, size, variant, ...rest }, ref) => {
	const chipStyles = useChipVariantStyle({ size, variant });
	const textStyle = useTextVariantStyle({ size, variant });

	return (
		<Box
			alignItems="center"
			backgroundColor="green.50"
			borderRadius={rem(60)}
			color="green.700"
			display="inline-flex"
			h="8"
			justifyContent="center"
			ref={ref}
			{...getFlaggedProps({ isInteractive })}
			{...chipStyles}
			{...rest}
		>
			<Box as="span" fontSize="xl" display="inline-flex" fontWeight="medium" lineHeight={rem(24)} pl={rem(10)} pr={rem(12)} py={rem(8)} {...textStyle}>
				{children}
			</Box>
		</Box>
	);
});
